import React, { useEffect, useCallback, useMemo } from 'react';
import NavBar from './NavBar';
import Body from './Body';
import { createLoadRecentBlogPostsAction } from '../actions/BlogPostActions'
import { createLoadTopNScoresLeaderboardAction, createLoadTopNPlaysLeaderboardAction, createLoadTopNNotechartArtistsLeaderboardAction, createGetUserPlaysOverTimeAction, createGetUserMaxDifficultyPassesOverTimeAction, createLoadTopNChartsPerYearLeaderboardAction, createLoadTopNChartsPerYearAndMonthLeaderboardAction, createLoadTopNSongsPerYearLeaderboardAction, createLoadTopNSongsPerYearAndMonthLeaderboardAction, createLoadUserScoresTakenLeaderboardAction, createGetUserSeasonsProgressAction, createGetUserRecommendedRivalsAction, createGetUserNextScoresToTakeAction, createGetUserPackProgressAction, createGetUserPlaysPerTimeOfDayAction, createLoadTopNPlaysPerCountryLeaderboardAction } from '../actions/StatsActions'
import { connect } from 'react-redux';
import { createGetProfileAction, createGetUserCanExportGdprReportAction, createGetUserCanRequestYearInReviewReportAction } from '../actions/ProfileActions'
import { RestHelper } from '../helpers/RestHelper';
import { LocalTokenHelper } from '../helpers/LocalTokenHelper';
import { createLogInUserLocallyAction, createLogOutUserAction, ILogOutUserAction, ILogInType } from '../actions/LogInActions';
import { IState } from '../state/State';

export interface IMainPageItemProps {  
  logInState: ILogInType;
  loadRecentBlogPosts: () => void;
  loadTopNScoresLeaderboard: () => void;
  loadTopNPlaysLeaderboard: () => void;
  loadTopNNotechartArtistsLeaderboard: () => void;
  loadTopNChartsPerYearLeaderboard: () => void;
  loadTopNChartsPerYearAndMonthLeaderboard: () => void;
  loadTopNSongsPerYearLeaderboard: () => void;
  loadTopNSongsPerYearAndMonthLeaderboard: () => void;
  getProfile: () => void;
  getUserPlaysOverTime: () => void;
  getUserPlaysPerTimeOfDay: () => void;
  getUserMaxDifficultyPassesOverTime: () => void;
  loadUserScoresTakenLeaderboard: () => void;
  getUserSeasonsProgress: () => void;
  getUserRecommendedRivals: () => void;
  getUserNextScoresToTake: () => void;
  logInUserLocally: () => void;
  logOutUser: () => ILogOutUserAction;
  getUserPackProgress: () => void;
  getUserCanExportGdprReport: () => void;
  getUserCanRequestYearInReviewReport: (years: number[]) => void;
  loadTopNPlaysPerCountryLeaderboard: () => void;
}

const MainPage: React.FC<IMainPageItemProps> = React.memo((props: IMainPageItemProps) => {
    // Memoize auth state check
    const isAuthenticated = useMemo(() => props.logInState.accessToken != null, [props.logInState.accessToken]);

    // Memoize years array outside effects to prevent recreation
    const years = useMemo(() => [
      2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018
    ], []);

    // Memoize the profile data fetching function
    const getProfileData = useCallback(async () => {
      if (!isAuthenticated) {
        return;
      }

      const loadUserData = () => {
        props.getProfile();
        props.getUserPlaysOverTime();
        props.getUserPlaysPerTimeOfDay();
        props.getUserMaxDifficultyPassesOverTime();
        props.getUserSeasonsProgress();
        props.getUserRecommendedRivals();
        props.getUserNextScoresToTake();
        props.getUserPackProgress();
        props.getUserCanExportGdprReport();
        props.getUserCanRequestYearInReviewReport(years);
      };

      try {
        const response = await RestHelper.get(`${RestHelper.RootUrl}/sdk/users/v1.0/user/profile`);
        if (response.ok) {
          loadUserData();
          return;
        }
        
        if (response.status === 401) {
          const refreshed = await RestHelper.refreshTokens();
          if (refreshed) {
            const retryResponse = await RestHelper.get(`${RestHelper.RootUrl}/sdk/users/v1.0/user/profile`);
            if (retryResponse.ok) {
              loadUserData();
            }
          }
        }
      } catch (error: any) {
        // Just log the error - never log out during profile fetch
        console.error('Error fetching profile:', error);
      }
    }, [
      isAuthenticated,
      props.getProfile,
      props.getUserPlaysOverTime,
      props.getUserPlaysPerTimeOfDay,
      props.getUserMaxDifficultyPassesOverTime,
      props.getUserSeasonsProgress,
      props.getUserRecommendedRivals,
      props.getUserNextScoresToTake,
      props.getUserPackProgress,
      props.getUserCanExportGdprReport,
      props.getUserCanRequestYearInReviewReport,
      props.logOutUser,
      years
    ]);

    // Effect for loading public data
    useEffect(() => {
      props.loadRecentBlogPosts();
      props.loadTopNScoresLeaderboard();
      props.loadTopNPlaysLeaderboard();
      props.loadTopNNotechartArtistsLeaderboard();
      props.loadTopNChartsPerYearAndMonthLeaderboard();
      props.loadTopNSongsPerYearAndMonthLeaderboard();
      props.loadUserScoresTakenLeaderboard();
      props.loadTopNPlaysPerCountryLeaderboard();
    }, []); // Run once on mount

    // Effect to restore login state from local storage
    useEffect(() => {
      const token = LocalTokenHelper.getLoginToken();
      if (token) {
        props.logInUserLocally();
      }
    }, []); // Only run once on mount

    // Separate effect for profile data loading
    useEffect(() => {
      if (isAuthenticated && props.logInState.accessToken) {
        getProfileData();
      }
    }, [isAuthenticated, props.logInState.accessToken, getProfileData]);

    return (
      <table className="MainPage">
        <tbody>
          <tr className="MainPageHeader"><NavBar /></tr>
          <tr className="MainPageBody"><Body /></tr>
        </tbody>
      </table>
    );
});

const actionCreators = {
  loadRecentBlogPosts: createLoadRecentBlogPostsAction,
  loadTopNScoresLeaderboard: createLoadTopNScoresLeaderboardAction,
  loadTopNPlaysLeaderboard: createLoadTopNPlaysLeaderboardAction,
  loadTopNNotechartArtistsLeaderboard: createLoadTopNNotechartArtistsLeaderboardAction,
  loadTopNChartsPerYearLeaderboard: createLoadTopNChartsPerYearLeaderboardAction,
  loadTopNChartsPerYearAndMonthLeaderboard: createLoadTopNChartsPerYearAndMonthLeaderboardAction,
  loadTopNSongsPerYearLeaderboard: createLoadTopNSongsPerYearLeaderboardAction,
  loadTopNSongsPerYearAndMonthLeaderboard: createLoadTopNSongsPerYearAndMonthLeaderboardAction,
  getProfile: createGetProfileAction,
  getUserPlaysOverTime: createGetUserPlaysOverTimeAction,
  getUserPlaysPerTimeOfDay: createGetUserPlaysPerTimeOfDayAction,
  getUserMaxDifficultyPassesOverTime: createGetUserMaxDifficultyPassesOverTimeAction,
  loadUserScoresTakenLeaderboard: createLoadUserScoresTakenLeaderboardAction,
  getUserSeasonsProgress: createGetUserSeasonsProgressAction,
  getUserRecommendedRivals: createGetUserRecommendedRivalsAction,
  getUserNextScoresToTake: createGetUserNextScoresToTakeAction,
  logInUserLocally: createLogInUserLocallyAction,
  logOutUser: createLogOutUserAction,
  getUserPackProgress: createGetUserPackProgressAction,
  getUserCanExportGdprReport: createGetUserCanExportGdprReportAction,
  getUserCanRequestYearInReviewReport: createGetUserCanRequestYearInReviewReportAction,
  loadTopNPlaysPerCountryLeaderboard: createLoadTopNPlaysPerCountryLeaderboardAction,
};

const mapStateToProps = (state: IState) => ({
  logInState: state.logIn.result
});

export default connect(mapStateToProps, actionCreators)(MainPage);
